import { Injectable } from '@angular/core';
import { PlessHttp } from 'src/app/shared/http/pless-http.service';
import { PlessRequest } from 'src/app/shared/vo/pless-request.model';
import { PlessJsonResponderSub } from 'src/app/commons/kymw00/pless-json-responder-sub.service';
import { PlessDialogServiceSub } from 'src/app/commons/kymw00/pless-dialog-service-sub';
import { KYMW7002Model, KYMW1001UserType } from '../kymw7002.model';
import { KYMW0300OutputModel } from '../../../../commons/kymw03/kymw0300/vo/kymw0300-output.model';

import type {
  GetCandidateSuggestionGoodsListCondition,
  GetCandidateSuggestionGoodsListCatalogInfoCondition,
  GetCandidateSuggestionGoodsListTagIdListCombination,
  GetCandidateSuggestionGoodsListGoodsMarkListCombination,
  GetCandidateSuggestionGoodsListCoopCodeListCombination,
  GetCandidateSuggestionGoodsListPCodeListCombination,
  GetCandidateSuggestionGoodsListItemCodeListCombination,
  GetCandidateSuggestionGoodsListDutiesListCombination,
  GetCandidateSuggestionGoodsListGuideGoodsNameListCombination,
  GetCandidateSuggestionGoodsListGuideStandardNameListCombination,
} from '../vo/get-candidate-suggestion-goods-list-condition.model';

// リクエスト構造
export interface GetCandidateSuggestionGoodsListRequest {
  search: {
    CandidateSuggestionGoodsList: {
      condition: GetCandidateSuggestionGoodsListCondition;
    };
    CatalogInfo: {
      condition: GetCandidateSuggestionGoodsListCatalogInfoCondition;
    };
    TagIdList?: {
      combination: {
        condition: GetCandidateSuggestionGoodsListTagIdListCombination;
      } [];
    };
    GoodsMarkList?: {
      combination: {
        condition: GetCandidateSuggestionGoodsListGoodsMarkListCombination;
      } [];
    };
    CoopCodeList?: {
      combination: {
        condition: GetCandidateSuggestionGoodsListCoopCodeListCombination;
      } [];
    };
    PCodeList?: {
      combination: {
        condition: GetCandidateSuggestionGoodsListPCodeListCombination;
      } [];
    };
    ItemCodeList?: {
      combination: {
        condition: GetCandidateSuggestionGoodsListItemCodeListCombination;
      } [];
    };
    DutiesList?: {
      combination: {
        condition: GetCandidateSuggestionGoodsListDutiesListCombination;
      } [];
    };
    GuideGoodsNameList?: {
      combination: {
        condition: GetCandidateSuggestionGoodsListGuideGoodsNameListCombination;
      } [];
    };
    GuideStandardNameList?: {
      combination: {
        condition: GetCandidateSuggestionGoodsListGuideStandardNameListCombination;
      } [];
    };
  };
}

// レスポンス構造
export interface GetCandidateSuggestionGoodsListResponse {
  /** 総件数 */
  TotalNum: string;
  /** 提案候補商品一覧 */
  CandidateSuggestionGoodsList: {
    /** Pコード */
    PCode: string;
    /** アイテムコード */
    ItemCode: string;
    /** JANコード */
    JanCode: string;
    /** 商品案内用商品名 */
    GuideGoodsName: string;
    /** 商品案内用規格名 */
    GuideStandardName: string;
    /** メーカー名 */
    MakerName: string;
    /** 訂正元eBaseNo */
    EBaseSpecCode: string;
    /** 過去企画回 */
    CatalogId: string;
  }[];
}

/**
 * 提案候補商品一覧情報 取得サービス
 * @export GetCandidateSuggestionGoodsListService
 * @class GetCandidateSuggestionGoodsListService
 * @extends {PlessJsonResponderSub}
 */
@Injectable()
export class GetCandidateSuggestionGoodsListService extends PlessJsonResponderSub<GetCandidateSuggestionGoodsListResponse> {
  model: KYMW7002Model;
  private requestData: GetCandidateSuggestionGoodsListRequest;
  private calbak: (response: GetCandidateSuggestionGoodsListResponse) => void;

  constructor(
    protected plessHttp: PlessHttp,
    protected dialogService: PlessDialogServiceSub
  ) {
    super(plessHttp, dialogService);
  }

  /**
   * bodyにセットする値を返す
   *
   * @private
   * @returns {PlessRequest} リクエストJSON
   * @memberof GetCandidateSuggestionGoodsListService
   */
  private getBody(request: KYMW0300OutputModel, pageNo: number): PlessRequest {
    // 通常検索
    const conditionCandidateSuggestionGoodsList: GetCandidateSuggestionGoodsListCondition = {
      OrderCode: this.model.orderCode ?? '',
      PCode: request.pcode ?? '',
      ItemCode: request.itemCode ?? '',
      JanCode: request.janCode ?? '',
      MakerName: request.makerName ?? '',
      TagSearchType: request.tagSearchType ?? '',
      GoodsMarkSearchType: request.goodsMarkSearchType ?? '',
      DataKbnFlg: request.dataKbnFlg ?? '',
      GoodsDiv: request.goodsDiv ?? '',
      BuyerCode: request.buyerCode ?? '',
      AdmitStatus: request.admitStatus ?? '',
      RegistDateFrom: request.registDateFrom ?? '',
      RegistDateTo: request.registDateTo ?? '',
      UpdateDateFrom: request.updateDateFrom ?? '',
      UpdateDateTo: request.updateDateTo ?? '',
      ClassCode: request.classCode ?? '',
      MiddleClassCode: request.middleClassCode ?? '',
      SmallClassCode: request.smallClassCode ?? '',
      OrdrClientCode: request.ordrClientCode ?? '',
      OrdrClientKanji: request.ordrClientKanji ?? '',
      PayeeClientCode: request.payeeClientCode ?? '',
      PayeeClientKanji: request.payeeClientKanji ?? '',
    } as GetCandidateSuggestionGoodsListCondition;

    const conditionCatalogInfo: GetCandidateSuggestionGoodsListCatalogInfoCondition = {
      SettingOrderCode: this.model.orderCode,
      ClientCode: '', 
      StartDispNum: pageNo, 
    } as GetCandidateSuggestionGoodsListCatalogInfoCondition;

    // ユーザー区分が「取引先（卸）」の場合、ログインユーザーのセッションに持つ取引先コードを設定する
    if (this.model.userType === KYMW1001UserType.WHOLESALE) {
      conditionCatalogInfo.ClientCode = this.model.supplierCode;
    }

    // リクエストのベースを作成
    this.requestData = {
      search: {
        CandidateSuggestionGoodsList: {
          condition: conditionCandidateSuggestionGoodsList
        },
        CatalogInfo: {
          condition: conditionCatalogInfo
        },
      }
    };

    // 以下、リスト検索（有効な配列データが 1 件以上存在した場合のみセットする）

    const combinationTagIdList = [] as { condition: GetCandidateSuggestionGoodsListTagIdListCombination }[];
    const tagIdList = request.tagId;
    if (tagIdList && Array.isArray(tagIdList)) {
      for (const tagId of tagIdList) {
        if (tagId) {
          combinationTagIdList.push({ condition: { TagId: tagId } });
        }
      }
    }
    if (combinationTagIdList.length > 0) {
      this.requestData.search.TagIdList = {
        combination: combinationTagIdList
      }
    }

    const combinationGoodsMarkList = [] as { condition: GetCandidateSuggestionGoodsListGoodsMarkListCombination }[];
    const goodsMarkList = request.goodsMark;
    if (goodsMarkList && Array.isArray(goodsMarkList)) {
      for (const goodsMark of goodsMarkList) {
        if (goodsMark) {
          combinationGoodsMarkList.push({ condition: { GoodsMark: goodsMark } });
        }
      }
    }
    if (combinationGoodsMarkList.length > 0) {
      this.requestData.search.GoodsMarkList = {
        combination: combinationGoodsMarkList
      }
    }

    const combinationCoopCodeList = [] as { condition: GetCandidateSuggestionGoodsListCoopCodeListCombination }[];
    const coopCodeList = request.coopCodeList;
    if (coopCodeList && Array.isArray(coopCodeList)) {
      for (const coopCode of coopCodeList) {
        if (coopCode) {
          combinationCoopCodeList.push({ condition: { CoopCode: coopCode } });
        }
      }
    }
    if (combinationCoopCodeList.length > 0) {
      this.requestData.search.CoopCodeList = {
        combination: combinationCoopCodeList
      }
    }

    const combinationPCodeList = [] as { condition: GetCandidateSuggestionGoodsListPCodeListCombination }[];
    const pCodeList = request.pcodeList;
    if (pCodeList && Array.isArray(pCodeList)) {
      for (const pCode of pCodeList) {
        if (pCode) {
          combinationPCodeList.push({ condition: { PCode: pCode } });
        }
      }
    }
    if (combinationPCodeList.length > 0) {
      this.requestData.search.PCodeList = {
        combination: combinationPCodeList
      }
    }

    const combinationItemCodeList = [] as { condition: GetCandidateSuggestionGoodsListItemCodeListCombination }[];
    const itemcodeList = request.itemcodeList;
    if (itemcodeList && Array.isArray(itemcodeList)) {
      for (const itemcode of itemcodeList) {
        if (itemcode) {
          combinationItemCodeList.push({ condition: { ItemCode: itemcode } });
        }
      }
    }
    if (combinationItemCodeList.length > 0) {
      this.requestData.search.ItemCodeList = {
        combination: combinationItemCodeList
      }
    }

    const combinationDutiesList = [] as { condition: GetCandidateSuggestionGoodsListDutiesListCombination }[];
    const dutiesList = request.duties;
    if (dutiesList && Array.isArray(dutiesList)) {
      for (const duties of dutiesList) {
        if (duties) {
          combinationDutiesList.push({ condition: { Duties: duties } });
        }
      }
    }
    if (combinationDutiesList.length > 0) {
      this.requestData.search.DutiesList = {
        combination: combinationDutiesList
      }
    }

    const combinationGuideGoodsNameList = [] as { condition: GetCandidateSuggestionGoodsListGuideGoodsNameListCombination }[];
    const guideGoodsNameList = request.guideGoodsNameList;
    if (guideGoodsNameList && Array.isArray(guideGoodsNameList)) {
      for (const guideGoodsName of guideGoodsNameList) {
        if (guideGoodsName) {
          combinationGuideGoodsNameList.push({ condition: { GuideGoodsName: guideGoodsName } });
        }
      }
    }
    if (combinationGuideGoodsNameList.length > 0) {
      this.requestData.search.GuideGoodsNameList = {
        combination: combinationGuideGoodsNameList
      }
    }

    const combinationGuideStandardNameList = [] as { condition: GetCandidateSuggestionGoodsListGuideStandardNameListCombination }[];
    const guideStandardNameList = request.guideStandardNameList;
    if (guideStandardNameList && Array.isArray(guideStandardNameList)) {
      for (const guideStandardName of guideStandardNameList) {
        if (guideStandardName) {
          combinationGuideStandardNameList.push({ condition: { GuideStandardName: guideStandardName } });
        }
      }
    }
    if (combinationGuideStandardNameList.length > 0) {
      this.requestData.search.GuideStandardNameList = {
        combination: combinationGuideStandardNameList
      }
    }

    return new PlessRequest(this.requestData);
  }

  /**
   * API呼び出し
   *
   * @param model
   * @memberof GetCandidateSuggestionGoodsListService
   */
  execute(model: KYMW7002Model, pageNo: number,  request: KYMW0300OutputModel, f?: (response: GetCandidateSuggestionGoodsListResponse) => void): void {
    this.model = model;
    if (f) this.calbak = f;
    this.send('getCandidateSuggestionGoodsList', this.getBody(request, pageNo));
  }

  /**
   * API通信成功時の処理
   *
   * @param {*} data レスポンスデータ
   * @memberof GetCandidateSuggestionGoodsListService
   */
  success(data: GetCandidateSuggestionGoodsListResponse) {
    if (this.calbak) this.calbak(data);
  }

}
